import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import ConfirmDialog, { confirmDialog } from '../../../components/ui/ConfirmDialog'
import AttachmentTable, { type AttachmentTableRow } from '../../../components/ui/AttachmentTable'
import { GRANTS } from '../../../auth/grants'
import FileUpload from '../../../components/ui/FileUpload'
import { useToast } from '../../../components/ui/ToastProvider'
import { deleteMeetingAttachment } from '../api/meetingApi'
import { uploadMinutesAttachments } from '../../minutes/api/minutesApi'
import { DOCUMENT_ACCEPT } from '../../../lib/uploadAccept'
import type { MeetingDocument } from '../types'

interface Props {
  meetingId: number
  documents: MeetingDocument[]
  /**
   * Dosya ekleme/silme yetkisi (`MEETING_FILE_MANAGE` + arşivli değil). `false` iken bölüm **aynı
   * tabloyla** render edilir, yalnız yükleme alanı ve satırdaki Kaldır ikonu çıkmaz.
   */
  canManage: boolean
}

/**
 * Toplantı Detay — Tutanaklar bölümü (044). Ortak {@link AttachmentTable} ile <b>Ad · Tür · Boyut ·
 * Yükleyen · Yüklenme</b> + satır bazında <b>İndir</b>; yetkiliyse ayrıca <b>Kaldır</b> (onaylı,
 * soft-delete) ve yükleme alanı. Yükleme <b>anında kaydeder</b> (Temel Bilgiler'den bağımsız; yeri
 * sabit) ve değişiklikte detay tazelenir.
 *
 * <h2>Tek tasarım (2026-08-25, kullanıcı isteği)</h2>
 *
 * Önce bu bölüm yetkiye göre <b>iki ayrı bileşene</b> dallanıyordu: yetkili tablo görürken yetkisiz
 * (ör. GM) kart listesi (`AttachmentList`) görüyordu — aynı veri iki farklı tasarımda. Artık tek yol
 * bu tablodur; yetki farkı yalnız <b>eylemleri</b> (yükleme alanı + Kaldır) gizler, sunumu değiştirmez.
 * Karşılaştırma için `AttachmentList` hâlâ Etkinlik Yönetici Özeti'nde kullanılıyor — orası ayrı bir
 * bağlam (salt-okuma özet kartı), bu değişiklik onu kapsamaz.
 */
export default function MeetingDocumentsSection({ meetingId, documents, canManage }: Readonly<Props>) {
  const { t } = useTranslation()
  const toast = useToast()
  const qc = useQueryClient()

  const refresh = () => qc.invalidateQueries({ queryKey: ['meeting-detail', meetingId] })

  const uploadMut = useMutation({
    mutationFn: (files: File[]) => uploadMinutesAttachments(meetingId, files),
    onSuccess: () => {
      refresh()
      toast.success(t('meetings.detail.documents.uploaded'))
    },
    onError: () => toast.error(t('meetings.detail.documents.uploadError')),
  })
  const deleteMut = useMutation({
    mutationFn: (attachmentId: number) => deleteMeetingAttachment(meetingId, attachmentId),
    onSuccess: () => {
      refresh()
      toast.success(t('meetings.detail.documents.removed'))
    },
    onError: () => toast.error(t('meetings.detail.documents.removeError')),
  })

  const busy = uploadMut.isPending || deleteMut.isPending

  const handleAdd = (files: File[]) => {
    if (files.length > 0) uploadMut.mutate(files)
  }
  const confirmRemove = (row: AttachmentTableRow) =>
    confirmDialog({
      group: 'doc-remove',
      header: t('meetings.detail.documents.removeConfirm.title'),
      message: t('meetings.detail.documents.removeConfirm.message', { name: row.fileName }),
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      acceptLabel: t('meetings.detail.edit.removeFile'),
      accept: () => deleteMut.mutate(Number(row.key)),
    })

  const rows: AttachmentTableRow[] = documents.map((d) => ({
    key: String(d.id),
    fileName: d.fileName,
    extension: d.extension,
    size: d.size,
    downloadId: d.id,
    uploadedByName: d.uploadedByName,
    uploadedAt: d.uploadedAt,
  }))

  return (
    <section className="minutes-section">
      <div className="minutes-section-head">
        <h2>{t('meetings.detail.documents.title')}</h2>
      </div>

      <AttachmentTable
        rows={rows}
        emptyText={t('meetings.detail.documents.empty')}
        onRemove={canManage ? confirmRemove : undefined}
        busy={busy}
        // Bağlam: ATTACHED_FILE.TABLE_NAME = 'MEETING' (toplantı tutanak dosyaları).
        requiredGrant={GRANTS.MEETING_FILE_DOWNLOAD}
      />

      {canManage && (
        <div className="mtg-doc-upload">
          <FileUpload
            files={[]}
            onChange={handleAdd}
            accept={DOCUMENT_ACCEPT}
            label={busy ? t('meetings.detail.documents.uploading') : t('minutes.documents.add')}
            hint={t('minutes.documents.hint')}
          />
        </div>
      )}

      {canManage && <ConfirmDialog group="doc-remove" />}
    </section>
  )
}
